import React, { useState, useEffect } from 'react';
import { useParams } from "react-router-dom";
import Footer from './Footer.js';
import LoadingAnimation from './LoadingAnimation.js';
import * as utils from '../common/Utils.js'
import { useConfig } from '../contexts/ConfigContext.js';
import { useIsAdmin } from '../hooks/useIsAdmin.js';

import { collection, getDocs } from "firebase/firestore";
import { db } from '../firebase.js';

const DATABASE_SOURCE = utils.getDatabaseSource();

const SignedUpPlayers = () => {
  const { playerID } = useParams();
  const config = useConfig();
  const CURRENT_SEASON = config.currentSeason;
  const isAdmin = useIsAdmin();
  const SEASON_NUMERAL = utils.toRomanNumeral(parseInt(CURRENT_SEASON));
  const [signups, setSignups] = useState([]);
  const [players, setPlayers] = useState([]);
  const [dataReady, setDataReady] = useState(false);

  useEffect(() => {
    if (!config.configLoaded) return;
    getPlayers();
    getSignups();
  }, [config.configLoaded, CURRENT_SEASON]) // eslint-disable-line react-hooks/exhaustive-deps

  const getPlayers = async () => {
    const playerSnapshot = await getDocs(collection(db, 'players'));
    const players = []
    playerSnapshot.forEach((doc) => {
      const player = doc.data()
      players.push(player)
    });

    setPlayers(players);
  }

  const getSignups = async () => {
    const signupsSnapshot = await getDocs(collection(db, "signups"));
    const signups = []

    // Used this code to add paid: true to all signups
    // signupsSnapshot.forEach(async (docSnapshot) => {
    //   const signupRef = doc(db, "signups", docSnapshot.id);
    //   await updateDoc(signupRef, { paid: true });
    // });

    signupsSnapshot.forEach((doc) => {
      const id = doc.id
      const signup = doc.data()
      signup.id = id
      signups.push(signup)
    });

    const thisSeasonSignups = signups.filter((signup) => signup.season == CURRENT_SEASON)

    let signupsSortedNewestToOldest = thisSeasonSignups.sort(function(s1,s2){
      let s1_date = new Date(s1.created_at.seconds * 1000 + s1.created_at.nanoseconds / 1000000)
      let s2_date = new Date(s2.created_at.seconds * 1000 + s2.created_at.nanoseconds / 1000000)
      return s2_date - s1_date;
    })
    setSignups(signupsSortedNewestToOldest)
    setDataReady(true);
  }

  function getPlayerByPlayerID (playerID) {
    return players.find((player) => player.id == playerID)
  }

  function displayPlayer (player, place=0, showJustName=false, signup) {
    if (!player) {
      if (showJustName) {
        return (<></>)
      } else {
        return (
          <div className="homepage-section">
            <p className="signup-empty">error with signup</p>
            <p className="signup-empty">player: { JSON.stringify(player) }</p>
            <p className="signup-empty">signup: { JSON.stringify(signup) }</p>
          </div>
        )
      }
    }

    if (showJustName) {
      return (
        <div className="signup-list-row" key={signup.id}>
          <p className="signup-list-name">
            <span className="signup-list-place">{ place + 1 }.</span>
            <a href={"/players/" + player.id }>{ utils.displayPlayerName(player) }</a>
          </p>
          <span className={`signup-tag signup-tag-${signup.status}`}>{ signup.status === 'new' ? 'New' : 'Returning' }</span>
        </div>
      )
    } else {
      const fee = signup?.status === 'returning' ? config.returningPlayerFee : config.newPlayerFee;
      return (
        <>
          <div className="homepage-section">
            <p className="mini-title">Welcome to League Morning Star</p>
            <p className="league-article-title">Pay your league fee to secure your spot</p>
            <p className="signup-body-text">To confirm your spot in Season { SEASON_NUMERAL }, <b>venmo ${ fee }</b> to <a href="https://account.venmo.com/u/morningstar-bball" target="_blank" rel="noreferrer">@morningstar-bball</a>. If you do not pay, your sign up will not be confirmed.</p>
            <div className="homepage-signup-row">
              <p className="article-read-time">Once you pay, your spot is secure.</p>
              <a className="signup-btn" href="https://account.venmo.com/u/morningstar-bball" target="_blank" rel="noreferrer">Pay ${ fee } on Venmo ›</a>
            </div>
          </div>
          <hr className="section-divider" />

          <div className="homepage-section">
            <p className="mini-title">Player Info</p>
            <dl className="signup-info">
              <div className="signup-info-row"><dt>Name</dt><dd>{ player.firstName } { player.lastName }</dd></div>
              <div className="signup-info-row"><dt>Email</dt><dd>{ player.email }</dd></div>
              <div className="signup-info-row"><dt>Phone</dt><dd>{ player.phone }</dd></div>
              <div className="signup-info-row"><dt>Height</dt><dd>{ player.height?.feet }' { player.height?.inches }"</dd></div>
            </dl>
          </div>
          <hr className="section-divider" />
        </>
      )
    }
  }

  function displayAllPlayers () {
    let newSignUps = signups.filter((signup) => signup.status === 'new')

    let signupsWithoutUndefinedPlayers = signups.filter((s) => getPlayerByPlayerID(s.player_id))

    return (
      <>
        <div className="homepage-section">
          <div className="homepage-section-title-row">
            <p className="mini-title">{ signups.length } Signed Up Player{ signups.length === 1 ? '' : 's' }</p>
            <p className="homepage-updated-at">{ newSignUps.length } new</p>
          </div>
          <div className="signup-list">
            { signupsWithoutUndefinedPlayers.map((signup, place) => displayPlayer(getPlayerByPlayerID(signup.player_id), place, true, signup)) }
          </div>
        </div>
        <hr className="section-divider" />
      </>
    )
  }

  if (dataReady == false) {
    return (
      <div className="HomepageUpdated signup-flow">
        <LoadingAnimation />
      </div>
    )
  }
  else {
    return (
      <div className="HomepageUpdated signup-flow">
        <div className="league-content">
          <div className="league-homepage">

            <nav className="homepage-nav">
              <p className="homepage-nav-label">Season { SEASON_NUMERAL } Sign Ups</p>
              { playerID && isAdmin && <a className="homepage-anchor-link" href="/signup/all">Signed Up Players</a> }
            </nav>
            <hr className="section-divider" />

            {
              playerID ?
              displayPlayer(getPlayerByPlayerID(playerID), 0, false, signups.find((signup) => signup.player_id === playerID))
              :
              displayAllPlayers()
            }

          </div>
        </div>
        <Footer/>
      </div>
    );
  }
}

export default SignedUpPlayers;
